import { FileTextOutlined, InboxOutlined } from '@ant-design/icons';
import { Alert, Card, Col, Collapse, DatePicker, Row, Spin, Statistic, Table, Tag, Typography, Upload } from 'antd';
import { useState } from 'react';
import type { Dayjs } from 'dayjs';
import axios from '../../config/axios';
import { formatNumber, handleAPIError } from '../../utils/helpers';
import AppLayout from '../../layouts/app.layout';

const { Dragger } = Upload;
const { Text } = Typography;

interface ReconEntry {
	zazu_id: number;
	zazu_status: string;
	statement_status: string;
	zazu_amount: number;
	statement_amount: number;
	phone: string;
	date: string;
	narrative: string;
	user: string;
	fee_adjusted?: boolean;
}

interface NoRecordEntry {
	zazu_id: number;
	narrative: string;
	statement_status: string;
	amount: number;
	phone: string;
	date: string;
}

interface FeeRow {
	narrative: string;
	amount: number;
	date: string;
}

interface OtherRow {
	type: string;
	narrative: string;
	amount: number;
	status: string;
	date: string;
}

interface ReconciliationResult {
	summary: {
		total_rows: number;
		matched: number;
		status_mismatch: number;
		amount_mismatch: number;
		no_record: number;
		telecom_fees_count: number;
		telecom_fees_total: number;
		other_count: number;
		other_total: number;
	};
	matched: ReconEntry[];
	status_mismatch: ReconEntry[];
	amount_mismatch: ReconEntry[];
	no_record: NoRecordEntry[];
	telecom_fees: { total_amount: number; count: number; rows: FeeRow[] };
	other_transactions: { total_amount: number; count: number; rows: OtherRow[] };
}

const statusTag = (status: string) => {
	if (status === 'SUCCEEDED' || status === 'success') return <Tag color="green">{status}</Tag>;
	if (status === 'FAILED' || status === 'failed') return <Tag color="red">{status}</Tag>;
	return <Tag color="orange">{status}</Tag>;
};

const reconColumns = [
	{ title: 'Zazu ID', dataIndex: 'zazu_id', key: 'zazu_id', width: 90 },
	{ title: 'User', dataIndex: 'user', key: 'user' },
	{ title: 'Phone', dataIndex: 'phone', key: 'phone', width: 130 },
	{
		title: 'Statement Amt',
		dataIndex: 'statement_amount',
		key: 'statement_amount',
		render: (v: number, row: ReconEntry) => (
			<span>
				UGX {formatNumber(v)}
				{row.fee_adjusted && <Tag color="blue" className="ml-1" style={{ fontSize: 10 }}>fee incl.</Tag>}
			</span>
		),
	},
	{
		title: 'Zazu Amt',
		dataIndex: 'zazu_amount',
		key: 'zazu_amount',
		render: (v: number) => `UGX ${formatNumber(v)}`,
	},
	{
		title: 'Statement Status',
		dataIndex: 'statement_status',
		key: 'statement_status',
		render: statusTag,
	},
	{
		title: 'Zazu Status',
		dataIndex: 'zazu_status',
		key: 'zazu_status',
		render: statusTag,
	},
	{ title: 'Date', dataIndex: 'date', key: 'date', width: 170 },
];

const Reconciliation = () => {
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<ReconciliationResult | null>(null);
	const [statementDate, setStatementDate] = useState<Dayjs | null>(null);

	const handleUpload = async (file: File) => {
		setLoading(true);
		setResult(null);
		const form = new FormData();
		form.append('file', file);
		try {
			const res = await axios.post('/admin/reconciliation', form, {
				withCredentials: true,
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			setResult(res.data.results);
		} catch (err: unknown) {
			handleAPIError(err as Error);
		} finally {
			setLoading(false);
		}
		return false;
	};

	const { summary } = result || {};

	return (
		<AppLayout title="Statement Reconciliation" subtitle="Upload a payment processor statement to match against Zazu transactions">
			<div className="space-y-6">
				<Card className="shadow-sm">
					<div className="mb-4">
						<Text strong className="mr-3">Statement Date</Text>
						<DatePicker
							value={statementDate}
							onChange={setStatementDate}
							placeholder="Select statement date"
							style={{ width: 200 }}
						/>
						{!statementDate && (
							<Text type="secondary" className="ml-3 text-sm">Select the date of this statement before uploading</Text>
						)}
					</div>
					<Dragger
						accept=".csv"
						showUploadList={false}
						beforeUpload={(file) => { handleUpload(file); return false; }}
						disabled={loading || !statementDate}
					>
						<p className="ant-upload-drag-icon"><InboxOutlined /></p>
						<p className="ant-upload-text">Click or drag a CSV statement file here</p>
						<p className="ant-upload-hint">
							{statementDate
								? `Uploading statement for ${statementDate.format('DD MMM YYYY')}`
								: 'Select a statement date above first'}
						</p>
					</Dragger>
					{loading && (
						<div className="mt-4 flex items-center justify-center gap-2">
							<Spin /> <Text type="secondary">Processing statement…</Text>
						</div>
					)}
				</Card>

				{result && summary && (
					<>
						{statementDate && (
							<Alert
								type="info"
								showIcon
								message={`Statement for ${statementDate.format('DD MMM YYYY')}`}
							/>
						)}

						{/* Summary */}
						<Row gutter={[16, 16]}>
							{[
								{ title: 'Total Rows', value: summary.total_rows, color: undefined },
								{ title: 'Matched', value: summary.matched, color: '#52c41a' },
								{ title: 'Status Mismatches', value: summary.status_mismatch, color: summary.status_mismatch > 0 ? '#fa8c16' : undefined },
								{ title: 'Amount Mismatches', value: summary.amount_mismatch, color: summary.amount_mismatch > 0 ? '#fa8c16' : undefined },
								{ title: 'No Zazu Record', value: summary.no_record, color: summary.no_record > 0 ? '#ff4d4f' : undefined },
								{ title: 'Telecom Fees', value: `UGX ${formatNumber(summary.telecom_fees_total)}`, color: undefined },
								{ title: 'Other Transactions', value: `UGX ${formatNumber(summary.other_total)}`, color: undefined },
							].map((s) => (
								<Col xs={12} sm={8} md={6} lg={4} key={s.title}>
									<Card size="small" className="shadow-sm text-center">
										<Statistic title={s.title} value={s.value} valueStyle={s.color ? { color: s.color } : undefined} />
									</Card>
								</Col>
							))}
						</Row>

						{/* Status Mismatches */}
						{result.status_mismatch.length > 0 && (
							<Card
								title={<span className="text-orange-500"><FileTextOutlined /> Status Mismatches ({result.status_mismatch.length})</span>}
								className="shadow-sm"
							>
								<Alert
									type="warning"
									showIcon
									message="These transactions have conflicting status between the statement and Zazu. Investigate before taking action."
									className="mb-4"
								/>
								<Table
									dataSource={result.status_mismatch}
									columns={reconColumns}
									rowKey="zazu_id"
									size="small"
									scroll={{ x: true }}
									pagination={{ pageSize: 20 }}
								/>
							</Card>
						)}

						{/* Amount Mismatches */}
						{result.amount_mismatch.length > 0 && (
							<Card
								title={<span className="text-orange-500"><FileTextOutlined /> Amount Mismatches ({result.amount_mismatch.length})</span>}
								className="shadow-sm"
							>
								<Table
									dataSource={result.amount_mismatch}
									columns={reconColumns}
									rowKey="zazu_id"
									size="small"
									scroll={{ x: true }}
									pagination={{ pageSize: 20 }}
								/>
							</Card>
						)}

						{/* No Record */}
						{result.no_record.length > 0 && (
							<Card
								title={<span className="text-red-500"><FileTextOutlined /> No Zazu Record ({result.no_record.length})</span>}
								className="shadow-sm"
							>
								<Table
									dataSource={result.no_record}
									rowKey="zazu_id"
									size="small"
									scroll={{ x: true }}
									pagination={{ pageSize: 20 }}
									columns={[
										{ title: 'Zazu ID', dataIndex: 'zazu_id', key: 'zazu_id', width: 90 },
										{ title: 'Phone', dataIndex: 'phone', key: 'phone' },
										{ title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number) => `UGX ${formatNumber(v)}` },
										{ title: 'Statement Status', dataIndex: 'statement_status', key: 'statement_status', render: statusTag },
										{ title: 'Date', dataIndex: 'date', key: 'date' },
										{ title: 'Narrative', dataIndex: 'narrative', key: 'narrative' },
									]}
								/>
							</Card>
						)}

						{/* Telecom Fees */}
						<Card className="shadow-sm">
							<Collapse
								items={[{
									key: 'fees',
									label: (
										<span>
											Telecom / Collection Fees — <strong>UGX {formatNumber(result.telecom_fees.total_amount)}</strong>
											<Text type="secondary" className="ml-2">({result.telecom_fees.count} rows)</Text>
										</span>
									),
									children: (
										<Table
											dataSource={result.telecom_fees.rows}
											rowKey={(_r, i) => `fee-${i}`}
											size="small"
											pagination={{ pageSize: 20 }}
											columns={[
												{ title: 'Narrative', dataIndex: 'narrative', key: 'narrative' },
												{ title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number) => `UGX ${formatNumber(v)}` },
												{ title: 'Date', dataIndex: 'date', key: 'date' },
											]}
										/>
									),
								}]}
							/>
						</Card>

						{/* Other Transactions */}
						{result.other_transactions.count > 0 && (
							<Card className="shadow-sm">
								<Collapse
									items={[{
										key: 'other',
										label: (
											<span>
												Other Transactions (non-Zazu) — <strong>UGX {formatNumber(result.other_transactions.total_amount)}</strong>
												<Text type="secondary" className="ml-2">({result.other_transactions.count} rows)</Text>
											</span>
										),
										children: (
											<Table
												dataSource={result.other_transactions.rows}
												rowKey={(_r, i) => `other-${i}`}
												size="small"
												pagination={{ pageSize: 20 }}
												columns={[
													{ title: 'Type', dataIndex: 'type', key: 'type' },
													{ title: 'Narrative', dataIndex: 'narrative', key: 'narrative' },
													{ title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number) => `UGX ${formatNumber(v)}` },
													{ title: 'Status', dataIndex: 'status', key: 'status', render: statusTag },
													{ title: 'Date', dataIndex: 'date', key: 'date' },
												]}
											/>
										),
									}]}
								/>
							</Card>
						)}

						{/* Matched */}
						<Card className="shadow-sm">
							<Collapse
								items={[{
									key: 'matched',
									label: (
										<span className="text-green-600">
											Matched ✓ <Text type="secondary">({result.matched.length} transactions)</Text>
										</span>
									),
									children: (
										<Table
											dataSource={result.matched}
											columns={reconColumns}
											rowKey="zazu_id"
											size="small"
											scroll={{ x: true }}
											pagination={{ pageSize: 20 }}
										/>
									),
								}]}
							/>
						</Card>
					</>
				)}
			</div>
		</AppLayout>
	);
};

export default Reconciliation;
